import { getCookie } from '../../utils/cookie';

export const csrftoken = (): string => {
    return getCookie('csrftoken') ?? ''
}

/**
 * The body a refused or failed request is rejected with: the server's JSON when
 * there is some, otherwise `{ error }` carrying its text or its status.
 *
 * The helpers below used to `throw response.json()`, a promise that itself
 * rejected whenever the body was not JSON (an HTML 500 page, a plain-text
 * refusal). The reason was thrown away and the screen printed "Error: Error"
 * (audit C5). Always resolving to an object with `error` means every caller
 * that reads `e.error` gets something a person can act on.
 */
export const readError = async (response: Response): Promise<any> => {
    const text = await response.text().catch(() => '')
    const what = response.status === 403 ? 'Refused' : response.status === 401 ? 'Not signed in' : 'Request failed'
    const fallback = `${what} (HTTP ${response.status})`
    try {
        const data = JSON.parse(text)
        if (data && typeof data === 'object') {
            // An `error` the server sent is passed on as it is, structured or
            // not. Some endpoints name the reason `message` or `detail`; a
            // caller reads `error`, so make sure there is one.
            if (data.error !== undefined && data.error !== null && data.error !== '') return data
            const error = [data.error, data.message, data.detail].find((v) => typeof v === 'string' && v)
            return { ...data, error: error || fallback, status: response.status }
        }
    } catch {
        // not JSON; fall through
    }
    const plain = text && !text.trimStart().startsWith('<') ? text.trim().slice(0, 300) : ''
    return { error: plain || fallback, status: response.status }
}

export const fetchPost = async ({ url, headers, body }: { url: string; headers?: any; body?: any }) => {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'X-CSRFToken': csrftoken(),
            ...headers,
        },
        body: JSON.stringify(body),
    })
    if (response.ok) {
        if (response.headers.get('Content-Type') == 'text/csv') {
            const filename = response.headers.get('Content-Disposition').split('filename=')[1]
            const blob = await response.blob()
            const fileUrl = window.URL.createObjectURL(blob)
            const fileLink = document.createElement('a')
            fileLink.href = fileUrl
            fileLink.download = filename
            fileLink.click()
            return
        }
        return response.json()
    } else throw await readError(response)
}